import React, { useState } from 'react';
import { Square, Sun, Cloud, CloudRain, X } from 'lucide-react';
import { TRANSLATIONS } from '../constants';
import { SlideBorderStyle, Theme } from '../types';

export interface SlideBoxStyleSelectorProps {
  selectedStyle: SlideBorderStyle;
  onStyleChange: (style: SlideBorderStyle) => void;
  language: 'ko' | 'en';
  isDarkMode?: boolean;
  theme?: Theme;
  shadowType?: 'none' | 'light' | 'medium' | 'strong' | 'long-shadow';
  onShadowChange?: (shadow: 'none' | 'light' | 'medium' | 'strong' | 'long-shadow') => void;
  isMobileModalMode?: boolean;
}

const BOX_STYLES: SlideBorderStyle[] = [
  {
    id: 'none',
    name: 'noStyle',
    borderWidth: 0,
    borderStyle: 'none',
    borderRadius: 0,
    boxShadow: 'none',
    backgroundColor: 'transparent',
    padding: 0
  },
  {
    id: 'clean-minimal',
    name: 'cleanBox',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 8,
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(theme-primary, 0.05)',
    padding: 16
  },
  {
    id: 'modern-card',
    name: 'modernCard',
    borderWidth: 0,
    borderStyle: 'none',
    borderRadius: 16,
    boxShadow: '0 8px 32px rgba(0,0,0,0.12)',
    backgroundColor: 'rgba(theme-secondary, 0.08)',
    padding: 24
  },
  {
    id: 'bold-frame',
    name: 'boldFrame',
    borderWidth: 3,
    borderStyle: 'solid',
    borderRadius: 12,
    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
    backgroundColor: 'rgba(theme-accent, 0.1)',
    padding: 20
  },
  {
    id: 'dashed-creative',
    name: 'dashedCreative',
    borderWidth: 2,
    borderStyle: 'dashed',
    borderRadius: 8,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(theme-quaternary, 0.06)',
    padding: 18
  },
  {
    id: 'glow-effect',
    name: 'glowEffect',
    borderWidth: 0.5,
    borderStyle: 'solid',
    borderRadius: 20,
    boxShadow: '0 0 20px rgba(59, 130, 246, 0.3), 0 8px 32px rgba(0,0,0,0.1)',
    backgroundColor: 'rgba(theme-primary, 0.08)',
    padding: 24
  },
  {
    id: 'frame',
    name: 'frame',
    borderWidth: 20,
    borderStyle: 'solid',
    borderRadius: 12,
    boxShadow: '0 8px 24px rgba(0,0,0,0.2)',
    customStyle: {
      borderImageSlice: 1,
      borderImageSource: 'linear-gradient(45deg, var(--theme-primary), var(--theme-secondary))',
      padding: '32px',
    }
  },
  {
    id: 'postit',
    name: 'postIt',
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: 2,
    boxShadow: '5px 5px 10px rgba(0,0,0,0.2)',
    customStyle: {
      backgroundColor: '#fffacd',
      transform: 'rotate(-1deg) scale(0.85)',
      margin: '16px',
    }
  },
  {
    id: 'polaroid',
    name: 'analog', // polaroid에서 analog로 변경
    borderWidth: 10,
    borderStyle: 'solid',
    borderRadius: 0,
    boxShadow: '0 4px 8px rgba(0,0,0,0.3)',
    customStyle: {
      borderColor: '#f0f0f0',
      borderBottomWidth: 40,
      backgroundColor: '#ffffff',
    }
  },
  {
    id: 'glassmorphic',
    name: 'glassMorphic',
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: 20,
    boxShadow: '0 8px 32px rgba(0,0,0,0.08)',
    customStyle: {
      borderColor: 'rgba(255,255,255,0.18)',
      backgroundColor: 'rgba(255,255,255,0.05)',
      backdropFilter: 'blur(10px)',
      padding: '24px',
    }
  },
  {
    id: 'bullet',
    name: 'bullet',
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: 0,
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
    customStyle: {
      borderTopLeftRadius: 0,
      borderBottomLeftRadius: 0,
      borderTopRightRadius: 24,
      borderBottomRightRadius: 24,
    }
  },
  {
    id: 'burnt',
    name: 'burnt',
    borderWidth: 4,
    borderStyle: 'solid',
    borderRadius: 4,
    boxShadow: '0 0 20px rgba(139,69,19,0.4)',
    customStyle: {
      borderColor: '#8B4513',
      filter: 'drop-shadow(0 0 3px #8B4513)',
    }
  },
  {
    id: 'hole',
    name: 'hole',
    borderWidth: 8,
    borderStyle: 'solid',
    borderRadius: 20,
    boxShadow: 'inset 0 0 20px rgba(0,0,0,0.5), 0 0 20px rgba(0,0,0,0.3)',
    customStyle: {
      borderColor: '#2a2a2a',
    }
  },
  {
    id: 'pencil',
    name: 'pencil',
    borderWidth: 2,
    borderStyle: 'solid',
    borderRadius: 8,
    boxShadow: '2px 2px 6px rgba(0,0,0,0.1)',
    customStyle: {
      borderColor: '#4a4a4a',
      borderImageSource: `url("data:image/svg+xml,%3Csvg width='100' height='100' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M0,0 Q50,5 100,0 T100,100 T0,100 T0,0' stroke='%234a4a4a' stroke-width='2' fill='none'/%3E%3C/svg%3E")`,
      borderImageSlice: 2,
    }
  }
];

export const SLIDE_BORDER_STYLES = BOX_STYLES;

export const SlideBoxStyleSelector: React.FC<SlideBoxStyleSelectorProps> = ({
  selectedStyle,
  onStyleChange,
  language,
  isDarkMode = false,
  theme,
  shadowType = 'none',
  onShadowChange,
  isMobileModalMode = false,
}) => {
  const t = TRANSLATIONS[language];
  const [currentShadow, setCurrentShadow] = useState(shadowType);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleShadowChange = (newShadow: typeof shadowType) => {
    setCurrentShadow(newShadow);
    onShadowChange?.(newShadow);
  };
  
  const handleStyleChange = (style: SlideBorderStyle) => {
    onStyleChange(style);
    if (isMobileModalMode) {
      setIsModalOpen(false);
    }
  };
  
  // 모바일 모달 모드일 때 표시할 버튼
  if (isMobileModalMode) {
    return (
      <>
        <button
          onClick={() => setIsModalOpen(true)}
          className="w-full h-full flex flex-col items-center justify-center gap-1"
        >
          <div 
            className={`w-10 h-6 ${
              isDarkMode 
                ? 'bg-gradient-to-r from-gray-600 to-gray-500' 
                : 'bg-gradient-to-r from-blue-100 to-purple-100'
            }`}
            style={{
              borderWidth: selectedStyle.borderWidth ? `${Math.min(selectedStyle.borderWidth, 3)}px` : 0,
              borderStyle: selectedStyle.borderStyle,
              borderColor: selectedStyle.id === 'bold-frame' && theme ? theme.accent : (isDarkMode ? '#8B5CF6' : '#6366f1'),
              borderRadius: selectedStyle.borderRadius ? `${Math.min(selectedStyle.borderRadius, 8)}px` : 0,
              boxShadow: selectedStyle.boxShadow === 'none' ? 'none' : '0 1px 3px rgba(0,0,0,0.1)',
            }}
          />
          <span className={`text-[10px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
            {t[selectedStyle.name as keyof typeof t] || selectedStyle.name}
          </span>
        </button>
        
        {/* 모바일 모달 */}
        {isModalOpen && (
          <div className="fixed inset-0 z-50 pointer-events-none">
            {/* 상단 모달 */}
            <div 
              className={`absolute top-0 left-0 right-0 h-[18vh] pointer-events-auto transform transition-transform duration-300 ${
                isModalOpen ? 'translate-y-0' : '-translate-y-full'
              } ${
                isDarkMode ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } border-b shadow-lg flex flex-col`}
            >
              {/* 닫기 버튼 */}
              <button
                onClick={() => setIsModalOpen(false)}
                className={`absolute top-2 right-2 p-1 rounded-lg transition-colors z-10 ${
                  isDarkMode
                    ? 'hover:bg-gray-700 text-gray-400'
                    : 'hover:bg-gray-100 text-gray-600'
                }`}
              >
                <X className="w-4 h-4" />
              </button>
              
              {/* 모달 콘텐츠 */}
              <div className="flex-1 overflow-y-auto p-3">
                <h3 className={`text-sm font-medium mb-2 flex items-center gap-2 ${
                  isDarkMode ? 'text-gray-200' : 'text-gray-800'
                }`}>
                  <Square className="w-4 h-4" />
                  {'박스 스타일 선택'}
                </h3>
                
                <div className="grid grid-cols-3 gap-2">
                  {BOX_STYLES.map((style) => (
                    <button
                      key={style.id}
                      onClick={() => handleStyleChange(style)}
                      className={`
                        relative p-2 rounded-lg border-2 transition-all text-center
                        ${selectedStyle.id === style.id 
                          ? `border-blue-500 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'} shadow-md` 
                          : isDarkMode
                            ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                            : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                        }
                      `}
                    >
                      <div className="space-y-1">
                        <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          {t[style.name as keyof typeof t]}
                        </div>
                        
                        {/* 스타일 미리보기 */}
                        <div className="flex items-center justify-center">
                          <div 
                            className={`w-10 h-6 ${
                              isDarkMode 
                                ? 'bg-gradient-to-r from-gray-600 to-gray-500' 
                                : 'bg-gradient-to-r from-blue-100 to-purple-100'
                            }`}
                            style={{
                              borderWidth: style.borderWidth,
                              borderStyle: style.borderStyle,
                              borderColor: style.id === 'bold-frame' && theme ? theme.accent : (isDarkMode ? '#8B5CF6' : '#6366f1'),
                              borderRadius: style.borderRadius,
                              boxShadow: style.boxShadow,
                            }}
                          />
                        </div>
                      </div>
                      
                      {/* 선택 표시 */}
                      {selectedStyle.id === style.id && (
                        <div className="absolute -top-1 -right-1 w-3 h-3 bg-blue-500 rounded-full flex items-center justify-center">
                          <div className="w-1.5 h-1.5 bg-white rounded-full"></div>
                        </div>
                      )}
                    </button>
                  ))}
                </div>
              </div>
            </div>
            
            {/* 배경 클릭 영역 - 메인 화면은 클릭 가능 */}
            <div 
              className="absolute inset-0 pointer-events-auto"
              onClick={() => setIsModalOpen(false)}
              style={{ top: '20%' }}
            />
          </div>
        )}
      </>
    );
  }
  
  // 데스크톱 모드 (기존 코드)

  return (
    <div className={`${isDarkMode ? 'bg-gray-700 border-gray-600' : 'bg-white border-gray-200'} rounded-lg border p-3 space-y-3 transition-colors`}>
      <div className="flex items-center gap-2">
        <Square className={`w-4 h-4 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} />
        <h4 className={`text-sm font-semibold ${isDarkMode ? 'text-gray-200' : 'text-gray-800'}`}>{t.boxStyle}</h4>
      </div>
      
      <div className="grid grid-cols-2 gap-2">
        {BOX_STYLES.map((style) => (
          <button
            key={style.id}
            onClick={() => onStyleChange(style)}
            className={`
              relative p-3 rounded-lg border-2 transition-all text-left
              ${selectedStyle.id === style.id 
                ? `border-blue-500 ${isDarkMode ? 'bg-blue-900/20' : 'bg-blue-50'} shadow-md` 
                : isDarkMode
                  ? 'border-gray-600 hover:border-gray-500 hover:bg-gray-600'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
              }
            `}
          >
            <div className="space-y-2">
              <div className={`text-xs font-medium ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {t[style.name as keyof typeof t]}
              </div>
              
              {/* 스타일 미리보기 */}
              <div className="flex items-center justify-center">
                <div 
                  className={`w-12 h-8 ${
                    isDarkMode 
                      ? 'bg-gradient-to-r from-gray-600 to-gray-500' 
                      : 'bg-gradient-to-r from-blue-100 to-purple-100'
                  }`}
                  style={{
                    borderWidth: style.borderWidth,
                    borderStyle: style.borderStyle,
                    borderColor: style.id === 'bold-frame' && theme ? theme.accent : (isDarkMode ? '#8B5CF6' : '#6366f1'),
                    borderRadius: style.borderRadius,
                    boxShadow: style.boxShadow,
                  }}
                />
              </div>
            </div>
            
            {/* 선택 표시 */}
            {selectedStyle.id === style.id && (
              <div className="absolute -top-1 -right-1 w-4 h-4 bg-blue-500 rounded-full flex items-center justify-center">
                <div className="w-2 h-2 bg-white rounded-full"></div>
              </div>
            )}
          </button>
        ))}
      </div>

      {/* 그림자 옵션 */}
      <div className="space-y-2 mt-4">
        <h5 className={`text-xs font-semibold ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>그림자 효과</h5>
        <div className="flex gap-2 flex-wrap">
          <button
            onClick={() => handleShadowChange('none')}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              currentShadow === 'none'
                ? 'bg-blue-500 text-white'
                : isDarkMode
                  ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t.shadowNone}
          </button>
          <button
            onClick={() => handleShadowChange('light')}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              currentShadow === 'light'
                ? 'bg-blue-500 text-white'
                : isDarkMode
                  ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Sun className="w-3 h-3 inline mr-1" />
            {t.shadowLight}
          </button>
          <button
            onClick={() => handleShadowChange('medium')}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              currentShadow === 'medium'
                ? 'bg-blue-500 text-white'
                : isDarkMode
                  ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <Cloud className="w-3 h-3 inline mr-1" />
            {t.shadowMedium}
          </button>
          <button
            onClick={() => handleShadowChange('strong')}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              currentShadow === 'strong'
                ? 'bg-blue-500 text-white'
                : isDarkMode
                  ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            <CloudRain className="w-3 h-3 inline mr-1" />
            {t.shadowStrong}
          </button>
          <button
            onClick={() => handleShadowChange('long-shadow')}
            className={`px-3 py-1 text-xs rounded-lg transition-all ${
              currentShadow === 'long-shadow'
                ? 'bg-blue-500 text-white'
                : isDarkMode
                  ? 'bg-gray-600 text-gray-300 hover:bg-gray-500'
                  : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
            }`}
          >
            {t.shadowLong}
          </button>
        </div>
      </div>
    </div>
  );
};